import {Headers, Http, RequestOptions, Response, URLSearchParams} from '@angular/http';
import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {Model} from './model.class';
import {Page} from './search/page.class';
import {UserImpersonationService} from '../user/user-impersonation.service';
import M = Service.InjectedMembers;
import ProjectionInformation = Model.ProjectionInformation;

export const IMPERSONATED_USER_PARAM = 'impersonatedUser';

export enum HttpMethod {GET, PUT, POST}

/**
 * Created by Rodrigo Quesada on 09/12/16.
 */
export class Service {

    private readonly baseUrl: string;
    private readonly baseOptions = new RequestOptions({
        headers: new Headers({'Content-Type': 'application/json'})
    });

    protected projectionInformationOverride?: ProjectionInformation;

    constructor(private readonly svcm: M, namespace: string) {
        this.baseUrl = `${process.env.BACKEND_BASE_URI}/api/${namespace}`;
    }

    getScalarValue<T>(path?: string, projectionOrModel?: ProjectionInformation|Model.Type<any>, ignoreUserImpersonation: boolean = false): Observable<T> {
        return this._afterRefresh(ignoreUserImpersonation, () => {
            let projection: ProjectionInformation|undefined;
            if (projectionOrModel) {
                projection = (projectionOrModel instanceof ProjectionInformation)
                    ? projectionOrModel
                    : (projectionOrModel as Model.Type<any>).projectionInformation;
            }
            return this.svcm.http.get(this.urlForPath(path), this.optionsFor(projection)).map(res => res.json().value);
        });
    }

    call(path?: string, params?: URLSearchParams, body?: any, method: HttpMethod = HttpMethod.GET, ignoreUserImpersonation: boolean = false): Observable<Response> {
        return this._afterRefresh(ignoreUserImpersonation, () => {
            switch (method) {
                case HttpMethod.GET:
                    return this.svcm.http.get(this.urlForPath(path), this.optionsFor(undefined, params));
                case HttpMethod.PUT:
                    return this.svcm.http.put(this.urlForPath(path), body, this.optionsFor(undefined, params));
                case HttpMethod.POST:
                    return this.svcm.http.post(this.urlForPath(path), body, this.optionsFor(undefined, params));
            }
        });
    }

    exists<T extends Model<T>>(type: Model.Type<T>, path?: string): Observable<boolean> {
        return this._afterRefresh(false, () => {
            let params = new URLSearchParams();
            params.set('existenceOnly', 'true');
            return this.svcm.http.get(this.urlForPath(path), this.optionsFor(type.projectionInformation, params)).map(res => {
                return res.json();
            });
        });
    }

    get<T extends Model<T>>(type: Model.Type<T>, path?: string, projection?: ProjectionInformation): Observable<T> {
        return this._afterRefresh(false, () => {
            return this.svcm.http.get(this.urlForPath(path), this.optionsFor(projection ? projection : type.projectionInformation)).map(res => {
                const jsonData = res.text().length ? res.json() : undefined;
                const exists = !!jsonData;
                const model = this._project(new type(jsonData), projection);
                model.exists = exists;
                return model;
            });
        });
    }

    update(model: Model<any>, path?: string, projection?: ProjectionInformation): Observable<Response> {
        return this._afterRefresh(false, () => {
            return this.svcm.http.post(this.urlForPath(path), model, this.optionsFor(
                projection
                    ? projection
                    : model.subProjectionInformation ? model.subProjectionInformation : model.TYPE.projectionInformation
            ));
        });
    }

    delete(type: Model.Type<any>, path?: string): Observable<Response> {
        return this._afterRefresh(false, () => {
            return this.svcm.http.delete(this.urlForPath(path), this.optionsFor(type.projectionInformation));
        });
    }

    getList<T extends Model<T>>(type: Model.Type<T>, path?: string, projection?: ProjectionInformation, extraParams?: URLSearchParams): Observable<Array<T>> {
        return this._afterRefresh(false, () => {
            return this.svcm.http.get(this.urlForPath(path), this.optionsFor(projection ? projection : type.projectionInformation, extraParams)).map(res => {
                return res.json().map(e => this._project(new type(e)), projection);
            });
        });
    }

    getPage<T extends Model<T>>(type: Model.Type<T>, path?: string, projection?: ProjectionInformation, extraParams?: URLSearchParams): Observable<Page<T>> {
        return this._afterRefresh(false, () => {
            return this.svcm.http.get(this.urlForPath(path), this.optionsFor(projection ? projection : type.projectionInformation, extraParams)).map(res => {
                return new Page(res.json(), e => this._project(new type(e), projection));
            });
        });
    }

    count<T extends Model<T>>(type: Model.Type<T>, path?: string): Observable<number> {
        return this._afterRefresh(false, () => {
            let params = new URLSearchParams();
            params.set('count', 'true');
            return this.svcm.http.get(this.urlForPath(path), this.optionsFor(type.projectionInformation, params)).map(res => res.json());
        });
    }

    private _afterRefresh<R>(ignoreUserImpersonation: boolean, body: () => Observable<R>): Observable<R> {
        return !ignoreUserImpersonation ? this.svcm.impersonationService.dataRefreshed$.first().flatMap(body) : body();
    }

    //region Utils
    private _project(model: Model<any>, projection?: ProjectionInformation) {
        if (projection) model.subProjectionInformation = projection;
        return model;
    }

    private urlForPath(path?: string): string {
        return path !== undefined ? `${this.baseUrl}/${path}` : this.baseUrl;
    }

    private optionsFor(projectionInfo?: ProjectionInformation, extraParams?: URLSearchParams) {
        let params = new URLSearchParams();
        if (extraParams) params.appendAll(extraParams);
        if (this.projectionInformationOverride) projectionInfo = this.projectionInformationOverride;
        if (projectionInfo) params.set('projection', projectionInfo.name);

        const impersonatedUserId = this.svcm.impersonationService.impersonatedUserId;
        if (impersonatedUserId && this.svcm.impersonationService.isImpersonationAllowedOnPage) params.append(IMPERSONATED_USER_PARAM, impersonatedUserId);

        return this.baseOptions.merge({search: params});
    }

    //endregion
}

// region Injection
export module Service {
    @Injectable()
    export class InjectedMembers {
        constructor(readonly http: Http, readonly impersonationService: UserImpersonationService) {
        }
    }
}
// endregion



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./~/tslint-loader!./src/app/domain/model/common/service.class.ts